import { useState } from "react";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white border border-ink/10 rounded-2xl px-5 py-3.5 text-sm text-ink placeholder:text-slate/70 focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo transition-[box-shadow,border-color] duration-200";

export default function PayInvoice() {
  const [form, setForm] = useState({ name: "", email: "", reference: "", amount: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount < 1) {
      toast.error("Please enter a valid amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          reference: form.reference,
          amount,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout failed");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section className="pt-[72px] hero-grid-bg min-h-screen" data-testid="pay-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-24 sm:pt-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Secure payment</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-ink leading-[1.08]">
            Pay your <span className="text-gradient">invoice</span>.
          </h1>
          <p className="mt-6 text-slate text-base sm:text-lg leading-relaxed max-w-md">
            The fast, secure way to pay Avora Business Solutions online. Enter your details and the amount on your invoice — you'll be taken to Stripe's secure checkout to complete payment by card.
          </p>
          <div className="mt-8 space-y-4">
            {[
              "Payments processed securely by Stripe",
              "Visa, Mastercard & Amex accepted",
              "Receipt emailed to you instantly",
            ].map((t) => (
              <p key={t} className="flex items-center gap-3 text-sm text-ink/75">
                <ShieldCheck className="w-4 h-4 text-mint shrink-0" /> {t}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-white rounded-[2rem] border border-ink/5 shadow-[0_24px_64px_rgba(20,22,46,0.1)] p-8 sm:p-10">
            <form onSubmit={submit} className="space-y-5" data-testid="pay-form">
              <div>
                <label htmlFor="pay-name" className="block text-sm font-medium text-ink mb-2">Your name *</label>
                <input id="pay-name" data-testid="pay-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Citizen" className={inputCls} />
              </div>
              <div>
                <label htmlFor="pay-email" className="block text-sm font-medium text-ink mb-2">Email for your receipt *</label>
                <input id="pay-email" data-testid="pay-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="jane@business.com.au" className={inputCls} />
              </div>
              <div>
                <label htmlFor="pay-reference" className="block text-sm font-medium text-ink mb-2">Invoice / reference number</label>
                <input id="pay-reference" data-testid="pay-reference-input" value={form.reference} onChange={set("reference")} placeholder="e.g. INV-0042" className={inputCls} />
              </div>
              <div>
                <label htmlFor="pay-amount" className="block text-sm font-medium text-ink mb-2">Amount (AUD) *</label>
                <div className="relative">
                  <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate text-sm font-medium">$</span>
                  <input id="pay-amount" data-testid="pay-amount-input" type="number" min="1" step="0.01" required value={form.amount} onChange={set("amount")} placeholder="0.00" className={`${inputCls} pl-9`} />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="pay-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-indigo text-white font-semibold px-8 py-4 rounded-full shadow-[0_12px_32px_rgba(91,95,239,0.35)] hover:bg-ink transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {loading ? "Taking you to secure checkout..." : "Pay securely with Stripe"}
              </button>
              <p className="text-center text-xs text-slate flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> 256-bit encrypted checkout by Stripe
              </p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
